<template>
  <teleport to="head">
    <MetaTags
      :title="`IP addresses used by ${domain}`"
      :description="`These are the IPv4 and IPv6 addresses for ${domain}, according to the authoritative DNS server.`"
      :url="`https://www.nslookup.io/domains/${domainPunycode}/webservers/`"
    />
  </teleport>
  <HeaderComponent :domain="domain" :domain-search-to-url="(encodedDomain) => `/domains/${encodedDomain}/webservers/`" search-button-text="Find IP addresses" />
  <div class="flex-grow bg-neutral-100">
    <main class="container mx-auto max-w-6xl">
      <AdUnit class="mx-auto" :config="WebsiteToIpResultsTop" />
      <div class="flex flex-col justify-between mt-8 mb-6 ml-6 sm:flex-row sm:items-center">
        <h1 class="text-3xl">IP addresses for <b v-if="domain === domainPunycode">{{ domain }}</b><span v-else><b>{{ domainPunycode }}</b> ({{ domain }})</span></h1>
        <dns-records-link :domain="domain" class="shrink-0 hidden mr-6 sm:block xl:mr-0">
          <button type="button" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">All DNS records</button>
        </dns-records-link>
      </div>
      <div class="bg-white shadow p-6 pb-10 rounded-t">
        <AdUnit class="float-right ml-6" :config="WebsiteToIpResultsSide" />
        <ApiErrorAlert v-if="error !== null" :error="error" :domain="domain" dnsserver="authoritative" />
        <div v-else-if="loading" aria-hidden="true">
          <SkeletonComponent class="w-56 h-8 mb-6" />
          <div v-for="n in 2" :key="n">
            <SkeletonComponent v-for="n in 2" :key="n" class="inline-block w-1/2 h-4 pr-[10%] bg-clip-content" />
          </div>
          <SkeletonComponent class="inline-block w-56 h-8 my-6" />
          <SkeletonComponent class="w-24 h-4 mb-2" />
          <SkeletonComponent class="w-1/2 h-4 mb-6" />
          <template v-for="n in 3" :key="n">
            <SkeletonComponent class="w-24 h-4 mb-2 block" />
            <SkeletonComponent class="w-1/2 h-4 mb-2 block" />
            <SkeletonComponent class="w-1/2 h-4 mb-6" />
          </template>
        </div>
        <template v-else>
          <p class="mb-3">Our DNS servers responded with these IP addresses when we queried it for the domain {{ domain }}. Some DNS servers may return different IP addresses based on your location.</p>
          <div v-if="queryResultA.response.answer.length === 0 && queryResultAaaa.response.answer.length === 0" class="mt-4">
            <Alert type="warning">
              <span><span class="font-semibold">{{ domain }}</span> does not have any IP addresses.</span>
            </Alert>
          </div>
          <ip-addresses v-else :a-records="queryResultA" :aaaa-records="queryResultAaaa" />
          <h2 class="mt-8 mb-2 font-semibold text-2xl clear-right">Question and response</h2>
          <div class="flex flex-col gap-6 lg:flex-row">
            <div class="min-w-0">
              <raw-records :records="queryResultA" />
            </div>
            <div class="min-w-0">
              <raw-records :records="queryResultAaaa" />
            </div>
          </div>
        </template>
        <AdUnit class="mt-8 mx-auto" :config="WebsiteToIpResultsBottom" />
        <h2 class="mt-8 mb-2 font-semibold text-2xl">Find the IP addresses for another website or domain name</h2>
        <div class="flex flex-col lg:flex-row">
          <p class="shrink max-w-xl">With <router-link to="/website-to-ip-lookup/" class="text-blue-600 hover:text-blue-800 hover:underline">website to IP lookup</router-link>, you can find the IP addresses for any domain name
            or subdomain. When you enter the domain in the input field below, it will show whether or not IP addresses are configured for that domain.</p>
          <form class="mt-3 w-[500px] max-w-full sm:px-6 lg:mt-0" autocomplete="off">
            <DomainSearch placeholder-text="Domain name" emphasize buttonText="Find IP addresses" :prefill="domain" @submitted="redirectTo" />
          </form>
        </div>
      </div>
    </main>
  </div>
  <FooterComponent />
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios, { Canceler } from 'axios';
import { domainNameToDisplayName, domainNameToUrlPath, urlPathToDomainName } from '@/common/domainName';
import HeaderComponent from '@/components/layout/HeaderComponent.vue';
import FooterComponent from '@/components/layout/FooterComponent.vue';
import DomainSearch from '@/components/DomainSearch.vue';
import Alert from '@/components/micro/AlertComponent.vue';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import RawRecords from '@/components/micro/RawRecords.vue';
import MetaTags from '@/components/MetaTags.vue';
import SkeletonComponent from '@/components/micro/SkeletonComponent.vue';
import { ApiErrorType, toErrorType } from '@/model/ApiErrorAlert';
import ApiErrorAlert from '@/components/ApiErrorAlert.vue';
import IpAddresses from '@/components/records/IpAddresses.vue';
import {
  QueryResult, WebserversResponse, emptyDnsServer, emptyResponse,
} from '@/model/Api';
import AdUnit from '@/components/ads/AdUnit.vue';
import { WebsiteToIpResultsTop, WebsiteToIpResultsSide, WebsiteToIpResultsBottom } from '@/common/adSlots';

const props = defineProps({
  routerDomain: {
    type: String,
    required: true,
  },
});

const router = useRouter();

const domain = ref('');
const domainPunycode = ref('');
const loading = ref(true);
const error = ref<ApiErrorType | null>(null);
const showSidebar = ref(window.innerWidth > 991);
const cancelToken = ref<Canceler | null>(null);
const queryResultA = ref<QueryResult>({
  query: {
    server: emptyDnsServer,
    domain: domain.value,
    recordType: 'A',
  },
  response: emptyResponse,
});
const queryResultAaaa = ref<QueryResult>({
  query: {
    server: emptyDnsServer,
    domain: domain.value,
    recordType: 'AAAA',
  },
  response: emptyResponse,
});

function redirectTo(newDomain: string): void {
  router.push(`/domains/${domainNameToUrlPath(newDomain)}/webservers/`);
}

function load() {
  loading.value = true;
  error.value = null;

  if (cancelToken.value) {
    cancelToken.value();
  }
  const axiosSource = axios.CancelToken.source();
  cancelToken.value = axiosSource.cancel;

  axios
    .post<WebserversResponse>('api/v1/webservers', {
      domain: domain.value,
    }, {
      cancelToken: axiosSource.token,
    })
    .then((response) => {
      if (props.routerDomain !== domainNameToUrlPath(response.data.punycodeDomain)) {
        router.replace({
          path: `/domains/${domainNameToUrlPath(response.data.punycodeDomain)}/webservers/`,
        });
      }
      domain.value = domainNameToDisplayName(response.data.unicodeDomain);
      domainPunycode.value = domainNameToDisplayName(response.data.punycodeDomain);
      queryResultA.value = response.data.a;
      queryResultAaaa.value = response.data.aaaa;
      loading.value = false;
    })
    .catch((e) => {
      if (!axios.isCancel(e)) {
        error.value = toErrorType(e.response.data.error);
      }
    });
}

watch(() => props.routerDomain, () => {
  const domainName = urlPathToDomainName(props.routerDomain);
  domain.value = domainName;
  domainPunycode.value = domainName;
  load();
}, {
  immediate: true,
});

window.addEventListener('resize', () => {
  showSidebar.value = window.innerWidth > 991;
});
</script>
